import {useCustomPage} from '@common/custom-page/use-custom-page';
import React, {Fragment, Suspense, useEffect} from 'react';
import {PageMetaTags} from '@common/http/page-meta-tags';
import {PageStatus} from '@common/http/page-status';
import {CustomPage} from '@common/admin/custom-pages/custom-page';
import {FormProvider, useForm} from 'react-hook-form';
import {useUpdateCustomPage} from '@common/admin/custom-pages/requests/use-update-custom-page';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import { ChatEditorStickyHeader } from './chat-editor/chat-editor-sticky-header';
import { ChatEditorTitle } from './chat-editor/chat-editor-title';

const ChatBodyEditor = React.lazy(
  () => import('./chat-editor/chat-body-editor'),
);

export function UwCustomPage({message}: {message: any}) {
  return (
    <Fragment>
      <ChatContent message={message}/>
    </Fragment>
  );
}

export interface CreateChatPayload {
  body?: string;
}

interface ChatContentProps {
  message: any
}

// Convert plain text to HTML with minimized empty lines
export const formatMessageToHtml = (text: string): string => {
  // Normalize text by removing excessive newlines and trimming
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0); // Remove empty lines

  let html = '';
  let inList = false;

  // Helper to parse inline Markdown (bold, italic)
  const parseInlineMarkdown = (line: string): string => {
    // Handle **bold**
    line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Handle *italic*
    line = line.replace(/\*(.*?)\*/g, '<em>$1</em>');
    return line;
  };

  lines.forEach((line, index) => {
    // Handle headers with ###
    if (line.startsWith('###')) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const headerText = line.replace(/^###\s*/, ''); // Remove ###
      html += `<h3>${parseInlineMarkdown(headerText)}</h3>`;
    }
    // Handle numbered headers ending with :
    else if (line.match(/^\d+\.\s*.*:$/)) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<h4>${parseInlineMarkdown(line)}</h4>`;
    }
    // Handle bullet points
    else if (line.startsWith('- ')) {
      if (!inList) {
        html += '<ul>';
        inList = true;
      }
      const bulletText = line.replace('- ', '');
      html += `<li>${parseInlineMarkdown(bulletText)}</li>`;
    }
    // Handle paragraphs
    else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `<p>${parseInlineMarkdown(line)}</p>`;
    }
  });

  // Close any open list
  if (inList) html += '</ul>';

  return html;
};

function ChatContent({message}: ChatContentProps) {
  const navigate = useNavigate();
  const crupdatePage = useUpdateCustomPage();
  const form = useForm<CreateChatPayload>({
    defaultValues: {
      body: message,
    },
  });

  useEffect(() => {
    form.setValue('body', message);
  }, [message])


  const initialContent = formatMessageToHtml(message);

  const handleSave = (editorContent: string) => {
    crupdatePage.mutate(
      {
        ...form.getValues(),
        body: editorContent,
      },
      {
        onSuccess: () => navigate('../..', {relative: 'path'}),
      },
    );
  };

  return (
    <Suspense fallback={<FullPageLoader />}>
      <ChatBodyEditor initialContent={initialContent}>
        {(content, editor) => (
          <FileUploadProvider>
            <FormProvider {...form}>
              {/* <ChatEditorTitle/> */}
              <ChatEditorStickyHeader
                editor={editor}
                backLink="../.."
                isLoading={crupdatePage.isPending}
                onSave={handleSave}
              />

              <div className="mx-20">
                <div className="prose dark:prose-invert mx-auto flex-auto">
                  {content}
                </div>
              </div>
            </FormProvider>
          </FileUploadProvider>
        )}
      </ChatBodyEditor>
    </Suspense>
  );
}
